/**
 * Authentication service with local email/password and OAuth support.
 *
 * Supports:
 * - Email/password authentication
 * - Google OAuth (direct)
 * - Microsoft OAuth (direct)
 * - Email verification with OTP
 * - Password reset with OTP
 */

import { api } from './api.js';

/**
 * Authentication configuration
 */
const env = import.meta.env || {};
const config = {
  // OAuth redirect URIs
  redirectUri: env.VITE_AUTH_REDIRECT_URI || window.location.origin + '/#/auth/callback',
  postLogoutRedirectUri: env.VITE_AUTH_POST_LOGOUT_URI || window.location.origin,

  // Token storage
  storageKey: 'auth',
};

/**
 * Identity providers
 * @type {Object}
 */
export const IdentityProvider = {
  LOCAL: 'local',
  GOOGLE: 'google',
  MICROSOFT: 'microsoft',
};

/**
 * Authentication state
 */
let authState = {
  isAuthenticated: false,
  user: null,
  accessToken: null,
  refreshToken: null,
  expiresAt: null,
};

/**
 * Auth state change listeners
 * @type {Set<Function>}
 */
const listeners = new Set();

/**
 * Subscribe to auth state changes
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export function onAuthStateChange(callback) {
  listeners.add(callback);
  // Immediately notify with current state
  callback(authState);
  return () => listeners.delete(callback);
}

/**
 * Notify all listeners of state change
 */
function notifyListeners() {
  listeners.forEach((callback) => {
    try {
      callback(authState);
    } catch (e) {
      console.error('Auth listener error:', e);
    }
  });
}

/**
 * Save auth state to storage
 */
function saveAuthState() {
  try {
    const data = {
      accessToken: authState.accessToken,
      refreshToken: authState.refreshToken,
      expiresAt: authState.expiresAt,
      user: authState.user,
    };
    localStorage.setItem(config.storageKey, JSON.stringify(data));
  } catch (e) {
    console.warn('Failed to save auth state:', e);
  }
}

/**
 * Load auth state from storage
 */
function loadAuthState() {
  try {
    const data = localStorage.getItem(config.storageKey);
    if (data) {
      const parsed = JSON.parse(data);

      // Parse expiration date safely
      let isExpired = true;
      if (parsed.expiresAt) {
        try {
          const expiresDate = new Date(parsed.expiresAt);
          // Check if date is valid (not NaN) and in the future
          if (!isNaN(expiresDate.getTime()) && expiresDate > new Date()) {
            isExpired = false;
          }
        } catch (e) {
          console.warn('Failed to parse expiresAt:', e);
        }
      }

      // Check if token is valid and not expired
      if (!isExpired && parsed.accessToken) {
        authState = {
          isAuthenticated: true,
          user: parsed.user,
          accessToken: parsed.accessToken,
          refreshToken: parsed.refreshToken,
          expiresAt: parsed.expiresAt,
        };
      } else if (parsed.refreshToken) {
        // Token expired but we have refresh token - try to refresh
        authState = {
          isAuthenticated: false,
          user: null,
          accessToken: null,
          refreshToken: parsed.refreshToken,
          expiresAt: null,
        };
      } else {
        clearAuthState();
      }
    }
  } catch (e) {
    console.warn('Failed to load auth state:', e);
    clearAuthState();
  }
}

/**
 * Clear auth state
 */
function clearAuthState() {
  authState = {
    isAuthenticated: false,
    user: null,
    accessToken: null,
    refreshToken: null,
    expiresAt: null,
  };
  localStorage.removeItem(config.storageKey);
}

/**
 * Parse user from JWT token
 * @param {string} token - JWT access token
 * @returns {Object} User object
 */
function parseUserFromToken(token) {
  try {
    const [, payload] = token.split('.');
    const decoded = JSON.parse(atob(payload));

    return {
      id: decoded.sub || decoded.oid,
      email: decoded.email || decoded.emails?.[0] || decoded.preferred_username,
      name: decoded.name,
      givenName: decoded.given_name,
      familyName: decoded.family_name,
      picture: decoded.picture,
      idp: decoded.idp || IdentityProvider.LOCAL,
      role: decoded.extension_Role,
      organizationId: decoded.extension_OrganizationId,
    };
  } catch (e) {
    console.warn('Failed to parse token:', e);
    return null;
  }
}

/**
 * Update auth state from API response
 * @param {Object} response - Auth response from API
 */
function updateAuthFromResponse(response) {
  // Handle both camelCase (expiresIn) and snake_case (expires_in) from API
  const expiresInSeconds = response.expiresIn || response.expires_in || 3600;
  const expiresAt = new Date(Date.now() + expiresInSeconds * 1000);

  // Also handle both token field name formats
  const accessToken = response.accessToken || response.access_token;
  const refreshToken = response.refreshToken || response.refresh_token;

  if (!accessToken) {
    console.error('No access token in auth response');
    return;
  }

  authState = {
    isAuthenticated: true,
    user: parseUserFromToken(accessToken),
    accessToken: accessToken,
    refreshToken: refreshToken,
    expiresAt: expiresAt.toISOString(),
  };

  saveAuthState();
  notifyListeners();
}

/**
 * Authentication service
 */
export const auth = {
  /**
   * Initialize auth service
   */
  initialize() {
    loadAuthState();
    notifyListeners();

    // Try to refresh if we have a refresh token but no valid access token
    if (authState.refreshToken && !authState.isAuthenticated) {
      this.refreshToken().catch(() => {
        clearAuthState();
        notifyListeners();
      });
    }
  },

  /**
   * Get current authentication state
   * @returns {Object}
   */
  getState() {
    return { ...authState };
  },

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    return authState.isAuthenticated;
  },

  /**
   * Check if user had a previous session (auth data exists in storage)
   * Used to differentiate between "never logged in" and "session expired"
   * @returns {boolean}
   */
  hadPreviousSession() {
    try {
      const data = localStorage.getItem(config.storageKey);
      return data !== null;
    } catch (e) {
      return false;
    }
  },

  /**
   * Get current user
   * @returns {Object|null}
   */
  getCurrentUser() {
    return authState.user;
  },

  /**
   * Get access token
   * @returns {string|null}
   */
  getAccessToken() {
    return authState.accessToken;
  },

  // ==========================================================================
  // Email/Password Authentication
  // ==========================================================================

  /**
   * Register a new user with email and password
   * @param {string} name - User's display name
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @param {string} [checkoutSessionId] - Optional Stripe checkout session ID for paid signups
   * @param {string} [companyName] - Optional company/organization name
   * @returns {Promise<Object>} Result message
   */
  async register(name, email, password, checkoutSessionId = null, companyName = null) {
    const payload = {
      name,
      email,
      password,
    };

    // Include checkout session ID if provided (from Stripe checkout)
    if (checkoutSessionId) {
      payload.checkoutSessionId = checkoutSessionId;
    }

    // Include company name if provided (for organization creation)
    if (companyName) {
      payload.companyName = companyName;
    }

    const response = await api.post('/v1/auth/register', payload);

    // Paid users receive tokens directly — auto-login
    if (response.accessToken || response.access_token) {
      updateAuthFromResponse(response);
      return { autoActivated: true };
    }

    return response;
  },

  /**
   * Verify email with OTP
   * @param {string} email - User's email
   * @param {string} otp - 6-digit OTP code
   * @returns {Promise<Object>} Auth tokens
   */
  async verifyEmail(email, otp) {
    const response = await api.post('/v1/auth/verify-email', {
      email,
      otp,
    });

    updateAuthFromResponse(response);
    return authState.user;
  },

  /**
   * Resend OTP
   * @param {string} email - User's email
   * @param {string} purpose - 'email_verification' or 'password_reset'
   * @returns {Promise<Object>} Result message
   */
  async resendOtp(email, purpose = 'email_verification') {
    const response = await api.post('/v1/auth/resend-otp', {
      email,
      purpose,
    });
    return response;
  },

  /**
   * Login with email and password
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<Object>} User object
   */
  async login(email, password) {
    const response = await api.post('/v1/auth/login', {
      email,
      password,
    });

    updateAuthFromResponse(response);
    return authState.user;
  },

  /**
   * Request password reset
   * @param {string} email - User's email
   * @returns {Promise<Object>} Result message
   */
  async forgotPassword(email) {
    const response = await api.post('/v1/auth/forgot-password', {
      email,
    });
    return response;
  },

  /**
   * Verify password reset OTP
   * @param {string} email - User's email
   * @param {string} otp - 6-digit OTP code
   * @returns {Promise<Object>} Reset token
   */
  async verifyResetOtp(email, otp) {
    const response = await api.post('/v1/auth/verify-reset-otp', {
      email,
      otp,
    });
    return response;
  },

  /**
   * Reset password
   * @param {string} resetToken - Reset token from verifyResetOtp
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Result message
   */
  async resetPassword(resetToken, newPassword) {
    const response = await api.post('/v1/auth/reset-password', {
      resetToken,
      newPassword,
    });
    return response;
  },

  // ==========================================================================
  // OAuth Authentication
  // ==========================================================================

  /**
   * Sign in with Google (direct OAuth)
   */
  async signInWithGoogle() {
    try {
      const response = await api.get('/v1/auth/google');
      window.location.href = response.url;
    } catch (e) {
      console.error('Google sign-in error:', e);
      throw e;
    }
  },

  /**
   * Sign in with Microsoft (direct OAuth)
   */
  async signInWithMicrosoft() {
    try {
      const response = await api.get('/v1/auth/microsoft');
      window.location.href = response.url;
    } catch (e) {
      console.error('Microsoft sign-in error:', e);
      throw e;
    }
  },

  /**
   * Handle OAuth callback
   * @param {string} provider - 'google' or 'microsoft'
   * @param {string} code - Authorization code
   * @param {string} state - State parameter
   * @returns {Promise<Object>} User object
   */
  async handleOAuthCallback(provider, code, state) {
    const response = await api.get(`/v1/auth/${provider}/callback`, {
      params: { code, state },
    });

    updateAuthFromResponse(response);
    return authState.user;
  },

  // ==========================================================================
  // Token Management
  // ==========================================================================

  /**
   * Refresh access token
   * @returns {Promise<boolean>}
   */
  async refreshToken() {
    if (!authState.refreshToken) {
      return false;
    }

    try {
      const response = await api.post('/v1/auth/refresh', {
        refreshToken: authState.refreshToken,
      });

      updateAuthFromResponse(response);
      return true;
    } catch (e) {
      console.error('Token refresh failed:', e);
      // Clear auth on refresh failure
      await this.signOut({ redirect: false });
      return false;
    }
  },

  /**
   * Sign out
   * @param {Object} options - Sign-out options
   */
  async signOut(options = {}) {
    const { redirect = true } = options;

    // Call logout endpoint to invalidate token on server
    try {
      if (authState.accessToken) {
        await api.post('/v1/auth/logout');
      }
    } catch (e) {
      // Ignore errors - we're logging out anyway
    }

    // Clear local state
    clearAuthState();
    notifyListeners();

    if (redirect) {
      window.location.hash = '#/login';
    }
  },

  // ==========================================================================
  // User Info
  // ==========================================================================

  /**
   * Get current user info from server
   * @returns {Promise<Object>} User object
   */
  async getMe() {
    const response = await api.get('/v1/auth/me');
    return response;
  },

  /**
   * Check if user has a specific role
   * @param {string} role - Role to check
   * @returns {boolean}
   */
  hasRole(role) {
    return authState.user?.role === role;
  },

  /**
   * Check if user has any of the specified roles
   * @param {string[]} roles - Roles to check
   * @returns {boolean}
   */
  hasAnyRole(roles) {
    return roles.includes(authState.user?.role);
  },

  /**
   * Get user's organization ID
   * @returns {string|null}
   */
  getOrganizationId() {
    return authState.user?.organizationId || null;
  },
};

// Initialize on module load
auth.initialize();

export default auth;
